/**
 * QuickConverterTool - Base class for specialized format conversion tools
 * 
 * Provides auto-convert on upload functionality with a fixed target format.
 * Each specialized tool (e.g., JpgToPngTool) extends this with specific config.
 * 
 * Refactored to use shared UI components for consistency with ImageConverterTool
 */

import { BaseTool } from '../../core/BaseTool';
import { ConversionManager } from '../image-converter/conversionManager';
import { Modal } from '../../ui/modal';
import { downloadBlob } from '../../utils/downloadHelper';
import { AppIcon } from '../image-generator/appIcon';
import { TaglineManager } from '../image-generator/taglineManager';
import { CookieConsentController } from '../../services/cookieConsentController';
import { UploadZone } from '../../ui/uploadZone';
import { FileInfoDisplay } from '../../ui/fileInfoDisplay';
import {
  getFormat,
  getToolTitle,
  FormatDefinition,
} from './conversionConfig';

export interface QuickConverterConfig {
  sourceFormat: string;
  targetFormat: string;
}

export class QuickConverterTool extends BaseTool {
  protected config: QuickConverterConfig;
  protected sourceFormatDef: FormatDefinition;
  protected targetFormatDef: FormatDefinition;
  
  private conversionManager: ConversionManager;
  private modal: Modal;
  private appIcon: AppIcon;
  
  // Shared UI components for consistency
  private uploadZone: UploadZone;
  private fileInfoDisplay: FileInfoDisplay;
  
  private currentFile: File | null = null;
  private currentImage: HTMLImageElement | null = null;

  constructor(config: QuickConverterConfig) {
    super();
    this.config = config;
    
    const sourceDef = getFormat(config.sourceFormat);
    const targetDef = getFormat(config.targetFormat);
    
    if (!sourceDef || !targetDef) {
      throw new Error(`Invalid format configuration: ${config.sourceFormat} -> ${config.targetFormat}`);
    }
    
    this.sourceFormatDef = sourceDef;
    this.targetFormatDef = targetDef;
    
    this.conversionManager = new ConversionManager();
    this.modal = new Modal();
    this.appIcon = new AppIcon(this.modal);
    new CookieConsentController();
    
    // Initialize shared UI components
    this.uploadZone = new UploadZone({
      containerId: 'imageUploadZone',
      fileInputId: 'fileInput',
      acceptTypes: this.sourceFormatDef.acceptTypes.join(','),
      onFileSelected: this.handleFile.bind(this),
      formatLabel: this.sourceFormatDef.label,
    });
    
    this.fileInfoDisplay = new FileInfoDisplay();
    
    // Register modal globally for thank you popup
    (window as any).appModal = this.modal;
  }

  getToolName(): string {
    return getToolTitle(this.config.sourceFormat, this.config.targetFormat);
  }

  async init(): Promise<void> {
    if (this.initialized) return;
    
    console.log(`Initializing ${this.getToolName()}...`);
    
    // Set random app icon
    this.appIcon.setRandomIcon();
    
    // Initialize tagline (appends random tagline to description)
    new TaglineManager();
    
    // Initialize shared UI components
    this.uploadZone.init();
    this.fileInfoDisplay.init();
    
    // Setup event listeners for additional buttons
    this.setupEventListeners();
    
    // Related tools are now rendered statically at build time for SEO
    
    this.setInitialized(true);
    console.log(`${this.getToolName()} initialized`);
  }

  private setupEventListeners(): void {
    // Convert button (manual trigger if needed)
    const convertBtn = document.getElementById('generateBtn');
    convertBtn?.addEventListener('click', () => {
      if (this.currentImage) {
        this.convertAndDownload();
      }
    });

    // Convert another button
    const convertAnotherBtn = document.getElementById('convertAnotherBtn');
    convertAnotherBtn?.addEventListener('click', () => {
      this.clearImage();
    });
  }

  private handleFile(file: File): void {
    // Validate file type matches source format
    const isValidType = this.sourceFormatDef.acceptTypes.some(
      (type) => file.type === type || file.type.includes(this.config.sourceFormat)
    );
    
    if (!isValidType) {
      alert(`Please upload a ${this.sourceFormatDef.label} image file.`);
      return;
    }

    // Validate file size (max 50MB)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('File is too large. Maximum size is 50MB.');
      return;
    }

    this.currentFile = file;
    
    // Load and show preview, then auto-convert
    this.loadImage(file)
      .then((img) => {
        this.currentImage = img;
        
        // Use shared UploadZone to show preview (consistent styling)
        this.uploadZone.showPreview(img.src);
        
        // Use shared FileInfoDisplay to show file info
        this.fileInfoDisplay.show({
          name: file.name,
          format: this.sourceFormatDef.label,
          width: img.naturalWidth,
          height: img.naturalHeight,
          size: file.size,
        });
        
        // Auto-convert immediately
        this.convertAndDownload();
      })
      .catch((error) => {
        console.error('Error loading image:', error);
        alert('Error loading image. Please try again.');
      });
  }

  private loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target?.result as string;
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  private async convertAndDownload(): Promise<void> {
    if (!this.currentImage || !this.currentFile) {
      return;
    }

    // Show converting state
    const convertBtn = document.getElementById('generateBtn') as HTMLButtonElement;
    const btnText = convertBtn?.querySelector('span');
    const statusSection = document.getElementById('conversionStatus');
    const statusText = document.getElementById('statusText');
    
    if (convertBtn) {
      convertBtn.disabled = true;
    }
    if (btnText) {
      btnText.textContent = 'Converting...';
    }
    if (statusSection) {
      statusSection.style.display = 'block';
    }
    if (statusText) {
      statusText.textContent = 'Converting your image...';
    }

    try {
      // Convert the image
      const convertedBlob = await this.conversionManager.convert(
        this.currentImage,
        this.config.targetFormat
      );

      // Hide the converting status
      if (statusSection) {
        statusSection.style.display = 'none';
      }

      // Download immediately
      this.downloadImage(convertedBlob);

      // Show thank you modal
      if ((window as any).appModal) {
        (window as any).appModal.showThankYou();
      }

      // Show success state
      this.showSuccessState(convertedBlob);

    } catch (error) {
      console.error('Error converting image:', error);
      alert('Error converting image. Please try again.');
      
      if (statusText) {
        statusText.textContent = 'Conversion failed. Please try again.';
      }
    } finally {
      // Reset button
      if (convertBtn) {
        convertBtn.disabled = false;
      }
      if (btnText) {
        btnText.textContent = `Convert to ${this.targetFormatDef.label}`;
      }
    }
  }

  private showSuccessState(_blob: Blob): void {
    const actionsSection = document.getElementById('quickConverterActions');
    if (actionsSection) {
      actionsSection.style.display = 'flex';
    }
  }

  private downloadImage(blob: Blob): void {
    if (!this.currentFile) return;

    const originalName = this.currentFile.name;
    const nameWithoutExt = originalName.substring(0, originalName.lastIndexOf('.')) || originalName;
    const ext = this.config.targetFormat === 'jpg' ? 'jpg' : this.config.targetFormat;
    const newFilename = `${nameWithoutExt}.${ext}`;
    
    // Use cross-browser compatible download helper
    downloadBlob(blob, newFilename);
  }

  private clearImage(): void {
    this.currentFile = null;
    this.currentImage = null;

    // Use shared components to clear UI (consistent behavior)
    this.uploadZone.clear();
    this.fileInfoDisplay.hide();
    
    // Reset tool-specific UI elements
    const statusSection = document.getElementById('conversionStatus');
    const actionsSection = document.getElementById('quickConverterActions');

    if (statusSection) statusSection.style.display = 'none';
    if (actionsSection) actionsSection.style.display = 'none';
  }

  destroy(): void {
    this.currentFile = null;
    this.currentImage = null;
    this.setInitialized(false);
    console.log(`${this.getToolName()} destroyed`);
  }
}
